

// ==================== FILE: /VoiceHistoryTable.jsx ====================
import { Button, Card, Space, Spin, Table, Tooltip, Typography } from "antd";
import { Link } from "react-router-dom";
import { PhoneOutlined, ShopOutlined, ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { cardStyle } from "./styles";
import { brand, semantic, neutral } from "../../../../theme/tokens";
import StatusTag from "../../../../components/cms/StatusTag";

const { Text } = Typography;

// Nhãn loại biến động điểm (bao trọn mọi loại)
const TYPE_MAP = {
  TRANSACTION: { label: "Giao dịch", color: "blue" },
  GIVEPOINT: { label: "NSX cộng điểm", color: "green" },
  EVENT: { label: "Sự kiện", color: "geekblue" },
  CHANGEPOINT: { label: "Điều chỉnh", color: "gold" },
  PROMOTION: { label: "Tích điểm CT", color: "cyan" },
  GIFTEXCHANGE: { label: "Đổi quà", color: "magenta" },
  EXPIRED: { label: "Hết hạn", color: "default" },
  DEDUCTPOINT: { label: "Trừ điểm", color: "volcano" },
};

const columns = [
  {
    title: "ID",
    dataIndex: "id",
    key: "id",
    fixed: 'left',
    width: 80,
    render: (_, __, index) => index + 1,
  },
  {
    title: "Chi nhánh",
    dataIndex: "branch_id",
    key: "branch_id",
    width: 200,
    ellipsis: true,
    render: (_, record) => (
      <Link 
        to={`/store-manager?search=${encodeURIComponent(record.branch_t?.name || "")}`} 
        style={{ color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}
      >
        <ShopOutlined style={{ color: neutral[400] }} />
        <Text className="hover:text-cyan-600 transition-colors">
          {record.branch_t?.name || "Không có dữ liệu"}
        </Text>
      </Link>
    ),
  },
  {
    title: "Loại",
    dataIndex: "type",
    key: "type",
    width: 140,
    render: (type) => {
      const info = TYPE_MAP[type] || { label: type || "—" };
      return <StatusTag>{info.label}</StatusTag>;
    },
  },
  {
    title: "Nội dung",
    dataIndex: "description",
    key: "description",
    width: 220,
    ellipsis: true,
    render: (text) => (
      <Tooltip title={text}>
        <span className="truncate block max-w-xs" style={{ color: neutral[800] }}>
          {text || "—"}
        </span>
      </Tooltip>
    ),
  },
  {
    title: "Mã đơn hàng",
    dataIndex: "ec_order_id",
    key: "ec_order_id",
    width: 180,
    render: (_, record) => (
      <Button
        type="link"
        className="p-0"
      >
        {record.order?.shop_order_id || ""}
      </Button>
    ),
  },
  {
    title: "Điểm",
    dataIndex: "point",
    key: "point",
    align: "center",
    width: 150,
    render: (_, record) => {
      // Tăng/giảm điểm là thông tin cần nhìn ra ngay -> đây là chỗ màu ĐÁNG dùng.
      const up = record.fluctuation_type === "INCREASE";
      const point = Number(record.point).toLocaleString("vi-VN");

      return (
        <StatusTag tone={up ? "success" : "danger"}>
          {up ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          {up ? `+${point}` : `-${point}`}
        </StatusTag>
      );
    },
  },
  {
    title: "Còn lại",
    dataIndex: "point_remaining",
    key: "point_remaining",
    align: "center",
    width: 120,
    render: (val, record) => {
      if (record.fluctuation_type === "INCREASE") {
        return val !== null && val !== undefined ? (
          <strong style={{ color: brand.accent }}>
            {Number(val).toLocaleString("vi-VN")}
          </strong>
        ) : (
          <span>-</span>
        );
      }
      return <span>-</span>;
    },
  },
  {
    title: "Hạn dùng",
    dataIndex: "expired_at",
    key: "expired_at",
    align: "center",
    width: 160,
    render: (val, record) => {
      if (record.fluctuation_type === "INCREASE") {
        return val ? (
          <span style={{ color: semantic.danger }}>
            {dayjs(val).format("DD/MM/YYYY HH:mm")}
          </span>
        ) : (
          <StatusTag>Vĩnh viễn</StatusTag>
        );
      }
      return <span>-</span>;
    },
  },
  {
    title: "Trạng thái",
    dataIndex: "status_send_gift",
    key: "status_send_gift",
    align: "center",
    width: 180,
    render: (_, record) => {
      let color = "default";
      let orderStatus = record.order_status;
      let orderStatusDescription = record.order_status_description;

      if (orderStatus === "Success") color = "success";
      else if (orderStatus === "Review") color = "processing";
      else if (orderStatus === "Rejected") color = "warning";

      return <StatusTag tone={color}>{orderStatusDescription}</StatusTag>;
    },
  },
  {
    title: "Ngày thực hiện",
    dataIndex: "created_at",
    key: "created_at",
    width: 200,
    fixed: "right",
    render: (_, record) => dayjs(record.created_at).format("DD/MM/YYYY HH:mm:ss"),
  },
];

export default function PointHistoryCustomer({ data, pagination, loading, onChange }) {
  return (
    <Card
      title={
        <>
          <PhoneOutlined style={{ marginRight: 8 }} />
          Lịch sử điểm {loading && <Spin size="small" />}
        </>
      }
      style={cardStyle}
    >
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total}`,
        }}
        onChange={onChange}
        scroll={{ x: "max-content" }}
        size="small"
      />
    </Card>
  );
}
